import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Package } from "lucide-react";
import { useCustomer, withAuth } from "../lib/CustomerContext";
import api from "../lib/api";
import SEO from "../lib/SEO";

const STATUS_LABELS = {
    pending: { label: "Pending", cls: "bg-amber-100 text-amber-700" },
    paid: { label: "Paid", cls: "bg-emerald-100 text-emerald-700" },
    fulfilled: { label: "Fulfilled", cls: "bg-blue-100 text-blue-700" },
    cancelled: { label: "Cancelled", cls: "bg-gray-200 text-gray-600" },
};

const PAYMENT_STATUS_LABELS = {
    unpaid: { label: "Unpaid", cls: "bg-amber-100 text-amber-700" },
    paid: { label: "Paid", cls: "bg-emerald-100 text-emerald-700" },
    refunded: { label: "Refunded", cls: "bg-gray-200 text-gray-600" },
};

export default function AccountOrders() {
    const { customer, loading } = useCustomer();
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [fetching, setFetching] = useState(true);

    useEffect(() => {
        if (!loading && !customer) navigate("/login?next=/account/orders");
    }, [customer, loading, navigate]);

    useEffect(() => {
        if (!customer) return;
        api.get("/customer/orders", withAuth())
            .then((r) => setOrders(Array.isArray(r.data) ? r.data : []))
            .catch(() => setOrders([]))
            .finally(() => setFetching(false));
    }, [customer]);

    if (loading || !customer) {
        return <main className="pt-32 pb-20 text-center text-ink-soft">Loading…</main>;
    }

    return (
        <main data-testid="account-orders-page" className="pt-24 md:pt-[10rem] pb-20 bg-[#FFF7FB] min-h-[70vh]">
            <SEO title="My orders — Cake Central" path="/account/orders" />
            <div className="max-w-4xl mx-auto px-4 sm:px-6">
                <Link
                    to="/account"
                    className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-ink-soft hover:text-blush mb-6"
                >
                    <ArrowLeft size={14} /> Back to account
                </Link>

                <h1 className="font-display text-4xl md:text-5xl text-ink mb-2 leading-none">My orders</h1>
                <p className="text-sm text-ink-soft mb-8">
                    {orders.length === 0 && !fetching ? "No orders yet." : `${orders.length} order${orders.length === 1 ? "" : "s"}`}
                </p>

                {fetching && <p className="text-center text-ink-soft py-10">Loading orders…</p>}

                {orders.length === 0 && !fetching && (
                    <div className="text-center py-16 bg-white border border-[#EAE3DE]">
                        <Package size={32} className="mx-auto mb-4 text-blush/60" />
                        <p className="text-ink-soft mb-4">You haven't placed any orders yet.</p>
                        <Link to="/shop" className="btn-pink">Browse the shop</Link>
                    </div>
                )}

                <div className="space-y-3">
                    {orders.map((o) => {
                        const s = STATUS_LABELS[o.status] || STATUS_LABELS.pending;
                        const ps = PAYMENT_STATUS_LABELS[o.payment_status] || PAYMENT_STATUS_LABELS.unpaid;
                        return (
                            <div key={o.id} className="bg-white border border-[#EAE3DE] p-5" data-testid={`order-row-${o.id}`}>
                                <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
                                    <div>
                                        <p className="font-display text-lg">{o.order_number}</p>
                                        <p className="text-xs text-ink-soft">
                                            {new Date(o.created_at).toLocaleDateString("en-AU", {
                                                day: "numeric", month: "long", year: "numeric",
                                            })}
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <div className="flex gap-1 justify-end">
                                            <span className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 ${s.cls}`}>{s.label}</span>
                                            <span className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 ${ps.cls}`}>{ps.label}</span>
                                        </div>
                                        <p className="font-display text-lg mt-1">${o.total.toFixed(2)} AUD</p>
                                    </div>
                                </div>
                                <div className="text-sm text-ink-soft space-y-1">
                                    {o.items.map((it, i) => (
                                        <p key={i}>
                                            {it.name} × {it.qty}
                                            {it.option && <span className="opacity-70"> ({it.option})</span>}
                                        </p>
                                    ))}
                                </div>
                                {o.tracking_token && (
                                    <div className="mt-3 pt-3 border-t border-[#EAE3DE]">
                                        <Link
                                            to={`/track/${o.tracking_token}`}
                                            className="text-xs tracking-[0.2em] uppercase text-blush hover:underline"
                                        >
                                            View order details →
                                        </Link>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </main>
    );
}
